/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description CloudflareUploadField
 */
import { useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';
import { Button, Upload, UploadProps } from 'antd';
import { Upload as UploadIcon } from '@icon-park/react';

import CloudflareUploadFieldRow from './CloudflareUploadFieldRow';
import useBlobUpload from 'hooks/mutate/useBlobUpload';
import AppEvents, { Events } from '@veritable/common/utils/appEvents';
import { formatUploadFileSize, getUploadedFileKey } from 'utils/uploadAttachment';
import { S3_BLOB_CATEGORY } from '@veritable/common/constants';

const uploadListStyle: React.CSSProperties = {
	marginTop: 4,
	display: 'flex',
	flexDirection: 'column',
	gap: 4,
};

type CloudflareUploadFieldProps = {
	value?: PatientPayment.ICloudflareUploadFile[];
	onChange: (value: PatientPayment.ICloudflareUploadFile[]) => void;
	placeholder: string;
	entityType?: EntityTypeEnum;
	disabled?: boolean;
	allowedFileTypes?: string[];
	maxFileSize?: number | null;
} & UploadProps;

const CloudflareUploadField: React.FC<CloudflareUploadFieldProps> = ({
	value,
	onChange,
	placeholder,
	disabled,
	entityType,
	allowedFileTypes,
	accept,
	maxFileSize,
	...props
}) => {
	const { beforeUpload: beforeUploadProp, ...uploadProps } = props;
	const files = value || [];
	const currentFilesRef = useRef(files);

	useEffect(() => {
		currentFilesRef.current = files;
	}, [files]);

	const syncFiles = useCallback(
		(nextFiles: PatientPayment.ICloudflareUploadFile[]) => {
			currentFilesRef.current = nextFiles;
			onChange(nextFiles);
		},
		[onChange],
	);
	const appendUploadedFile = useCallback(
		(file: PatientPayment.ICloudflareUploadFile) => {
			syncFiles(_.uniqBy([...currentFilesRef.current, file], getUploadedFileKey));
		},
		[syncFiles],
	);
	const removeSavedFile = useCallback(
		(file: PatientPayment.ICloudflareUploadFile) => {
			syncFiles(_.reject(currentFilesRef.current, (currentFile) => getUploadedFileKey(currentFile) === getUploadedFileKey(file)));
		},
		[syncFiles],
	);
	const handleUploadError = useCallback((errorMessage: string) => {
		AppEvents.publish(Events.Error, errorMessage);
	}, []);

	const { uploads, uploadAccept, uploadFiles, removeUpload, isUploading, activeUploadCount } = useBlobUpload({
		blobCategory: S3_BLOB_CATEGORY.entityattachment,
		entityType,
		allowedFileTypes,
		accept,
		maxFileSize,
		onFileUploaded: appendUploadedFile,
		onError: handleUploadError,
	});

	const beforeUpload = async (file: RcFile, fileList: RcFile[]) => {
		if (file !== fileList[0]) {
			return Upload.LIST_IGNORE;
		}

		if (beforeUploadProp) {
			const result = await beforeUploadProp(file, fileList);
			if (result === false || result === Upload.LIST_IGNORE) {
				return Upload.LIST_IGNORE;
			}
		}

		uploadFiles(fileList);

		return Upload.LIST_IGNORE;
	};

	return (
		<div>
			<Upload {...uploadProps} beforeUpload={beforeUpload} disabled={disabled} showUploadList={false} accept={uploadAccept}>
				<Button type='default' icon={<UploadIcon />} disabled={disabled}>
					{isUploading ? `${placeholder} (${activeUploadCount})` : placeholder}
				</Button>
			</Upload>

			{!_.isEmpty(uploads) && (
				<div style={uploadListStyle}>
					{_.map(uploads, (upload) => (
						<CloudflareUploadFieldRow
							key={upload.id}
							name={upload.fileName}
							meta={formatUploadFileSize(upload.size)}
							progress={upload.progress}
							status={upload.status === 'failed' ? 'exception' : 'active'}
							statusText={upload.status === 'failed' ? upload.error || 'Failed to upload file' : 'Uploading...'}
							statusType={upload.status === 'failed' ? 'danger' : 'secondary'}
							onRemove={upload.status === 'failed' ? () => removeUpload(upload.id) : undefined}
							disabled={disabled}
						/>
					))}
				</div>
			)}

			{!_.isEmpty(files) && (
				<div style={uploadListStyle}>
					{_.map(files, (file) => (
						<CloudflareUploadFieldRow
							key={file.uid}
							name={file.name}
							onRemove={!disabled ? () => removeSavedFile(file) : undefined}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export default CloudflareUploadField;
